/**
 * Shared colour roles (cast, paper, eyes, blush...) for the three art styles. Each world adds its own
 * roles in `content/worlds/<world>/palette.ts`; `ART_PALETTES` (worlds/index.ts) merges them all.
 * Hex values live ONLY here and in the world palettes, never inside figures.
 */
import type { ArtStyleId } from './types';

export type RolePalette = Record<ArtStyleId, Record<string, string>>;

export const BASE_PALETTE: RolePalette = {
  A: {
    paper: '#FFF8EC',
    ground: '#E9D9BD',
    catFur: '#EE9B4F',
    catStripe: '#C9692F',
    cream: '#FFF1DC',
    pink: '#EE8C8C',
    eye: '#3B2A25',
    collar: '#3F9C9A',
    bell: '#F2C14E',
    foxFur: '#E9783A',
    foxDark: '#5B3A33',
    foxTip: '#4C86C9',
    scarf: '#F2C14E',
    skin: '#E6A57C',
    hair: '#3C2622',
    sweater: '#3F9C9A',
    overall: '#F2C14E',
    glasses: '#C2513F',
    mouth: '#B24A4A',
    tongue: '#E57B7B',
    pencil: '#F2C14E',
    pencilWood: '#F1D2A8',
    eraser: '#EE8C8C',
    blush: '#F08A8A',
    white: '#FFFDF8',
    leaf: '#7DB86A',
    leafDark: '#4E8A5C',
    water: '#8FCFE0',
    sparkle: '#F7D35C',
  },
  B: {
    paper: '#FFFFFF',
    ground: '#D9D2F2',
    catFur: '#FF8E2B',
    catStripe: '#E5561B',
    cream: '#FFF3DD',
    pink: '#FF6B8E',
    eye: '#2A1E3F',
    collar: '#1FBFB0',
    bell: '#FFD12E',
    foxFur: '#FF7A2E',
    foxDark: '#3A2448',
    foxTip: '#3E7BFF',
    scarf: '#FFD12E',
    skin: '#F0A77A',
    hair: '#2E1C2E',
    sweater: '#7A5CFF',
    overall: '#FFD12E',
    glasses: '#FF4D6D',
    mouth: '#D93554',
    tongue: '#FF7A93',
    pencil: '#FFD12E',
    pencilWood: '#FFD9A8',
    eraser: '#FF6B8E',
    blush: '#FF5C82',
    white: '#FFFFFF',
    leaf: '#38C46A',
    leafDark: '#1E9E5A',
    water: '#3EB8F5',
    sparkle: '#FFD12E',
  },
  C: {
    paper: '#FFFBF7',
    ground: '#F3E1EA',
    catFur: '#FFCFA6',
    catStripe: '#F4A97E',
    cream: '#FFF8EE',
    pink: '#FFB0BF',
    eye: '#5A3B3B',
    collar: '#A9DCCB',
    bell: '#FFE08F',
    foxFur: '#FFB98C',
    foxDark: '#9B6F6A',
    foxTip: '#A9C8F5',
    scarf: '#FFE08F',
    skin: '#FAD2B6',
    hair: '#8A5E52',
    sweater: '#B9D8F2',
    overall: '#FFD6E0',
    glasses: '#E89AA8',
    mouth: '#E57D8E',
    tongue: '#FFA3B4',
    pencil: '#FFE08F',
    pencilWood: '#FBE3C8',
    eraser: '#FFB0BF',
    blush: '#FF9DB2',
    white: '#FFFFFF',
    leaf: '#B5E2B9',
    leafDark: '#9FD3B0',
    water: '#BFE3F2',
    sparkle: '#FFE08F',
  },
};

/** Merge role palettes; later ones win (world palettes must not redefine base roles, see validator). */
export function mergePalettes(...ps: RolePalette[]): RolePalette {
  return {
    A: Object.assign({}, ...ps.map((p) => p.A)),
    B: Object.assign({}, ...ps.map((p) => p.B)),
    C: Object.assign({}, ...ps.map((p) => p.C)),
  };
}

/* ------------------------------ derived skins ------------------------------ */

const toRgb = (h: string) => {
  const n = parseInt(h.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255) as [number, number, number];
};
const toHex = (r: number, g: number, b: number) => '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, '0')).join('');

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}
function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (!s) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t: number) => {
    const x = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    return x < 1 / 6 ? p + (q - p) * 6 * x : x < 1 / 2 ? q : x < 2 / 3 ? p + (q - p) * (2 / 3 - x) * 6 : p;
  };
  return [f(h + 1 / 3), f(h), f(h - 1 / 3)];
}

/** Style B (sticker pop): same hue, more saturated and a touch brighter. */
export function stickerize(hex: string): string {
  const [h, s, l] = rgbToHsl(toRgb(hex));
  return toHex(...hslToRgb([h, Math.min(1, s * 1.35 + 0.08), Math.min(0.92, l * 1.02)]));
}
/** Style C (soft pastel): mixed toward white, lighter and softer. */
export function pastelize(hex: string): string {
  const [h, s, l] = rgbToHsl(toRgb(hex));
  return toHex(...hslToRgb([h, Math.min(1, s * 0.85), l + (0.97 - l) * 0.5]));
}

/**
 * A world palette from its Storybook (A) colours: B and C are derived automatically (override any role
 * that needs hand-tuning). Keeps all three skins working with zero extra effort.
 */
export function rolePalette(A: Record<string, string>, overrides: { B?: Record<string, string>; C?: Record<string, string> } = {}): RolePalette {
  const map = (fn: (h: string) => string) => Object.fromEntries(Object.entries(A).map(([k, v]) => [k, fn(v)]));
  return { A: { ...A }, B: { ...map(stickerize), ...(overrides.B ?? {}) }, C: { ...map(pastelize), ...(overrides.C ?? {}) } };
}
